/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import React from "react";
import { IGeoRegion, IProfile } from "../../../../store/api/admin/types";
import ConfirmDialog from "../../../Shared/Dialogs/ConfirmDialog";
import { useEditProfile } from "./hooks";
import { DIALOGOPENFOR, OP_CODE_GROUPS } from "../../../../globals/globals";
import DataLoader from "../../../Shared/Loader/DataLoader";
import { constStr } from "../../../../globals/constants";
import { Button } from "../../../ui/button";
import { Checkbox } from "../../../ui/checkbox";
import { Sheet } from "../../../ui/sheet";
import { Field, FieldGroup, FieldLabel } from "../../../ui/field";
import { Icon } from "../../../Shared/Icon";
import { RadioGroup, RadioGroupItem } from "../../../ui/radio-group";
import { InputField } from "../../../Shared/Radix/Inputs/InputField";

interface IEditProfileProps {
  profile: IProfile;
  handleCancel: () => void;
  onDelete?: () => void;
}

interface ProfileRegionsFieldProps {
  accountRegions: IGeoRegion[];
  selectedRegions: string[];
  onRegionsChange: (regions: string[]) => void;
  isEditMode?: boolean;
}

type ApplicationOperationLevel = "none" | "view" | "manage";

interface ApplicationOperationRow {
  resource: string;
  viewKey: string;
  manageKey?: string;
  required?: boolean;
}

const ProfileRegionsField: React.FC<ProfileRegionsFieldProps> = ({
  accountRegions,
  selectedRegions,
  onRegionsChange,
  isEditMode = false,
}) => {
  const sortedCatalog = React.useMemo(
    () => [...accountRegions].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" })),
    [accountRegions],
  );

  const regionNames = sortedCatalog.map((region) => region.name);
  const allSelected = regionNames.length > 0 && regionNames.every((region) => selectedRegions.includes(region));
  const masterChecked: boolean = allSelected ? true : false;

  const toggleRegion = (name: string, enabled: boolean) => {
    if (enabled) {
      if (selectedRegions.includes(name)) return;
      onRegionsChange([...selectedRegions, name]);
      return;
    }

    onRegionsChange(selectedRegions.filter((region) => region !== name));
  };

  const toggleAll = (enabled: boolean) => {
    if (enabled) {
      onRegionsChange(regionNames);
      return;
    }

    onRegionsChange(selectedRegions.filter((region) => !regionNames.includes(region)));
  };

  return (
    <Field>
      <FieldLabel>Regions</FieldLabel>
      <div className="max-h-64 space-y-0 overflow-y-auto rounded-md border border-border p-3 mt-2">
        {(
          <label className="flex cursor-pointer items-center gap-3 border-b border-border pb-2.5 text-sm font-medium text-foreground">
            <Checkbox
              checked={masterChecked}
              onCheckedChange={(checked) => toggleAll(checked === true)}
              disabled={regionNames.length === 0}
              aria-label="All regions"
            />
            <span>
              All regions
              {regionNames.length > 0 ? ` (${String(regionNames.length)})` : ""}
            </span>
          </label>
        )}
        <div className={`flex flex-col gap-2 ${!isEditMode ? "pt-2.5" : ""}`}>
          {sortedCatalog.length === 0 ? (
            <p className="text-sm text-muted-foreground">No regions available.</p>
          ) : (
            sortedCatalog.map((region) => (
              <label
                key={region.objectKey || region.name}
                className="flex cursor-pointer items-center gap-3 text-sm text-muted-foreground"
              >
                <Checkbox
                  checked={selectedRegions.includes(region.name)}
                  onCheckedChange={(checked) => toggleRegion(region.name, checked === true)}
                  aria-label={region.name}
                />
                <span className="text-foreground">{region.name}</span>
              </label>
            ))
          )}
        </div>
      </div>
    </Field>
  );
};

const EditProfile: React.FC<IEditProfileProps> = ({ profile, handleCancel, onDelete }) => {
  const {
    editableProfile,
    geoRegions,
    name,
    handleNameChange,
    adminOpCodes,
    appOpCodes,
    setAppOpCodes,
    handleOnChangeAdminOperation,
    handleOnChangeAppOperation,
    saveButtonEnabled,
    handleSave,
    openConfirmDialog,
    handleClickSaveAction,
    handleRegionAutocompleteSelect,
  } = useEditProfile(profile);

  const applicationOperationRows = React.useMemo<ApplicationOperationRow[]>(() => {
    const rows: ApplicationOperationRow[] = [];
    const seen = new Set<string>();

    for (const [key, config] of Object.entries(OP_CODE_GROUPS)) {
      if (config.isAdmin || seen.has(key)) {
        continue;
      }

      if (config.child && OP_CODE_GROUPS[config.child]) {
        const viewKey = config.child;
        const viewConfig = OP_CODE_GROUPS[viewKey];
        rows.push({
          resource: (viewConfig.description || config.description).replace(/^(View|Manage)\s+/, ""),
          viewKey,
          manageKey: key,
          required: !!viewConfig.required,
        });
        seen.add(key);
        seen.add(viewKey);
        continue;
      }

      if (!config.parent || !OP_CODE_GROUPS[config.parent]) {
        rows.push({
          resource: config.description.replace(/^(View|Manage)\s+/, ""),
          viewKey: key,
          required: !!config.required,
        });
        seen.add(key);
      }
    }

    return rows;
  }, []);

  const getApplicationOperationLevel = (row: ApplicationOperationRow): ApplicationOperationLevel => {
    if (row.manageKey && appOpCodes.includes(row.manageKey)) {
      return "manage";
    }

    if (appOpCodes.includes(row.viewKey)) {
      return "view";
    }

    return "none";
  };

  const applyApplicationOperationLevel = (row: ApplicationOperationRow, level: ApplicationOperationLevel) => {
    const nextCodes = new Set(
      appOpCodes.filter((opCode) => opCode !== row.viewKey && opCode !== row.manageKey),
    );

    if (level !== "none" || row.required) {
      nextCodes.add(row.viewKey);
    }

    if (level === "manage" && row.manageKey) {
      nextCodes.add(row.manageKey);
    }

    setAppOpCodes(Array.from(nextCodes));
  };

  /* Adapter: Radix Checkbox onCheckedChange(checked) → hooks handler(event) */
  const onAdminCheckChange = (key: string, checked: boolean) => {
    handleOnChangeAdminOperation({ target: { checked, value: key } } as any);
  };

  const onAppCheckChange = (key: string, checked: boolean) => {
    handleOnChangeAppOperation({ target: { checked, value: key } } as any);
  };

  if (!editableProfile) return <DataLoader />;

  const selectedRegionNames = editableProfile.geoRegions?.map((region) => region.name) || [];

  const handleRegionsChange = (regions: string[]) => {
    const nextValue = geoRegions
      .filter((region) => regions.includes(region.name))
      .map((region) => ({ title: region.name, value: region.objectKey }));

    handleRegionAutocompleteSelect({ stopPropagation: () => undefined } as any, nextValue as any);
  };

  const canDeleteProfile = typeof editableProfile.id === "number" && editableProfile.id > 0;

  const footer = (
    <div className="flex w-full flex-wrap items-center justify-between gap-2">
      <div className="flex min-w-0 flex-1">
        {canDeleteProfile && onDelete ? (
          <Button
            type="button"
            variant="outline"
            className="gap-1.5 !text-destructive border-destructive/50 hover:bg-transparent hover:!text-destructive"
            onClick={onDelete}
          >
            <Icon name="delete" size={18} />
            Delete profile
          </Button>
        ) : null}
      </div>
      <div className="flex flex-row gap-2">
        <Button type="button" variant="outline" onClick={handleCancel}>
          Cancel
        </Button>
        <Button
          type="button"
          disabled={!saveButtonEnabled || !name.trim()}
          onClick={async () => {
            await handleSave(handleCancel);
          }}
        >
          Save
        </Button>
      </div>
    </div>
  );

  return (
    <>
      {openConfirmDialog === DIALOGOPENFOR.PROFILE_SAVE && (
        <ConfirmDialog
          actionName={constStr.yesSave}
          handleClickAction={async (actionName) => {
            await handleClickSaveAction(actionName, handleCancel);
          }}
        >
          Are you sure you want to save changes to <strong>{name || "Unknown"}</strong>?
          <p className="pt-3 pb-3">
            This could affect active users assigned to this profile.
          </p>
        </ConfirmDialog>
      )}

      <Sheet
        open={true}
        onClose={handleCancel}
        title={`${editableProfile.id === 0 ? "Add" : "Edit"} profile`}
        subtitle={
          editableProfile.id === 0
            ? "Name this profile and choose which regions and operations it may use."
            : "Update the profile name, regions, and operations for this access profile."
        }
        footer={footer}
        width="570px"
        useCardStyle={false}
        side="right"
      >
        <form autoComplete="off">
          <FieldGroup className="pt-2">
            <Field controlSize="md">
              <InputField
                required
                label="Profile Name"
                name="name"
                value={name}
                maxLength={100}
                placeholder="Enter profile name"
                onChange={(fieldName, value) => handleNameChange({ target: { value } } as any)}
                tooltipText="Maximum length: 100 characters"
                autoFocus={true}
              />
            </Field>

            {adminOpCodes.includes("system_admin") ? (
              <Field controlSize="md">
                <InputField
                  label="Regions"
                  name="regions"
                  value="All Regions"
                  disabled={true}
                  onChange={() => {}}
                />
              </Field>
            ) : (
              <ProfileRegionsField
                accountRegions={geoRegions}
                selectedRegions={selectedRegionNames}
                onRegionsChange={handleRegionsChange}
                isEditMode={(editableProfile.id ?? 0) > 0}
              />
            )}

            <Field>
              <FieldLabel>Admin operations</FieldLabel>
              <div className="max-h-48 space-y-0 overflow-y-auto rounded-md border border-border p-3 mt-2">
                <div className="flex flex-col gap-2">
                  {Object.entries(OP_CODE_GROUPS).map(
                    ([key, { description = "", isAdmin }]) =>
                      isAdmin && (
                        <label
                          key={key}
                          htmlFor={`admin-${key}`}
                          className="flex cursor-pointer items-center gap-3 text-sm"
                        >
                          <Checkbox
                            id={`admin-${key}`}
                            checked={adminOpCodes.includes(key)}
                            onCheckedChange={(checked) => onAdminCheckChange(key, checked === true)}
                          />
                          <span>{description ?? ""}</span>
                        </label>
                      ),
                  )}
                </div>
              </div>
            </Field>

            <Field>
              <FieldLabel>Application operations</FieldLabel>
              <div className="max-h-72 overflow-y-auto rounded-md border border-border bg-muted/10 px-3 py-2 mt-2">
                <div className="grid grid-cols-[minmax(0,1fr)_2.25rem_2.25rem_2.75rem] gap-x-2 border-b border-border/60 pb-2 text-xs font-medium text-muted-foreground">
                  <div>Operation</div>
                  <div className="text-left">None</div>
                  <div className="text-left">View</div>
                  <div className="text-left">Manage</div>
                </div>
                <div className="flex flex-col divide-y divide-border/50">
                  {applicationOperationRows.map((row) => {
                    const rowId = row.resource.replace(/\s+/g, "-").toLowerCase();
                    return (
                      <RadioGroup
                        key={row.viewKey}
                        value={getApplicationOperationLevel(row)}
                        onValueChange={(value) => applyApplicationOperationLevel(row, value as ApplicationOperationLevel)}
                        className="grid grid-cols-[minmax(0,1fr)_2.25rem_2.25rem_2.75rem] items-center gap-x-2 py-2 text-sm text-foreground"
                      >
                        <div className="min-w-0">{row.resource}</div>
                        <div className="flex justify-start">
                          <RadioGroupItem
                            value="none"
                            id={`${rowId}-none`}
                            aria-label={`${row.resource}: none`}
                            className="shrink-0"
                            disabled={!!row.required}
                          />
                        </div>
                        <div className="flex justify-start">
                          <RadioGroupItem
                            value="view"
                            id={`${rowId}-view`}
                            aria-label={`${row.resource}: view`}
                            className="shrink-0"
                          />
                        </div>
                        <div className="flex justify-start">
                          <RadioGroupItem
                            value="manage"
                            id={`${rowId}-manage`}
                            aria-label={`${row.resource}: manage`}
                            className="shrink-0"
                            disabled={!row.manageKey}
                          />
                        </div>
                      </RadioGroup>
                    );
                  })}
                </div>
              </div>
            </Field>
          </FieldGroup>
        </form>
      </Sheet>
    </>
  );
};

export default EditProfile;
